import { useEffect, useState } from 'react';
import { Link, Navigate, Route, Routes, useLocation, useNavigate, useParams } from 'react-router-dom';
import { api } from './api/client';
import type { Usage } from './api/types';
import { useSession } from './lib/session';
import { ADMIN_ROLES, ROLE_LABEL, UserRole } from './api/enums';
import { useT } from './i18n/locale';
import { LocaleSwitch } from './components/LocaleSwitch';
import { Spinner, UsageBar } from './components/ui';
import LoginScreen from './screens/LoginScreen';
import HomeScreen from './screens/HomeScreen';
import MenuScreen from './screens/MenuScreen';
import ScanScreen from './screens/ScanScreen';
import DraftsScreen from './screens/DraftsScreen';
import HistoryScreen from './screens/HistoryScreen';
import AccurateSettingsScreen from './screens/AccurateSettingsScreen';
import TeamScreen from './screens/TeamScreen';
import AccountScreen from './screens/AccountScreen';
import BillingScreen from './screens/BillingScreen';
import VerifyEmailScreen from './screens/VerifyEmailScreen';
import MockPayScreen from './screens/MockPayScreen';
import ForgotPasswordScreen from './screens/ForgotPasswordScreen';
import ResetPasswordScreen from './screens/ResetPasswordScreen';
import RequireRole from './components/RequireRole';

export default function App() {
  const { user, loading } = useSession();
  const t = useT();
  const location = useLocation();

  if (loading) {
    return (
      <div className="flex min-h-full items-center justify-center">
        <Spinner label={t('app.loading')} />
      </div>
    );
  }

  // Password reset works in any auth state — a link may be opened on a device
  // where someone else is signed in, or no one is — so it is handled before the
  // sign-in gate, with the language switch pinned on as on the other signed-out
  // screens.
  if (location.pathname === '/forgot-password' || location.pathname === '/reset-password') {
    return (
      <>
        <div className="fixed top-3 right-3 z-50">
          <LocaleSwitch />
        </div>
        {location.pathname === '/forgot-password' ? (
          <ForgotPasswordScreen />
        ) : (
          <ResetPasswordScreen />
        )}
      </>
    );
  }

  if (!user) {
    return (
      <>
        {/*
          Signed out there is no app header, so the switch is pinned to the
          corner — otherwise the one place a new user meets the app, the login
          and signup screens, would be the one place they cannot pick a
          language. The dark page background is what the white-on-transparent
          switch is styled for.
        */}
        <div className="fixed top-3 right-3 z-50">
          <LocaleSwitch />
        </div>
        <Routes>
          <Route path="/signup" element={<LoginScreen mode="signup" />} />
          {/* Reachable signed out: the link may be opened on another device. */}
          <Route path="/verify" element={<VerifyEmailScreen />} />
          <Route path="*" element={<LoginScreen mode="login" />} />
        </Routes>
      </>
    );
  }

  // A signed-in user landing on /login should go to the app, not see the form.
  if (location.pathname === '/login' || location.pathname === '/signup') {
    return <Navigate to="/" replace />;
  }

  /*
   * Unverified: the app is closed. Every route lands on the verification
   * screen so there is one obvious next step, and the trial cannot start
   * until the address is proven. The server enforces this too — this only
   * saves the user from hitting 403s.
   */
  if (user.emailVerified === false) {
    return (
      <div className="flex min-h-full flex-col">
        <TopBar />
        <main className="flex-1">
          <Routes>
            <Route path="*" element={<VerifyEmailScreen />} />
          </Routes>
        </main>
      </div>
    );
  }

  return (
    <div className="flex min-h-full flex-col">
      <TopBar />
      <main className="flex-1">
        {/*
          Keyed by pathname so each navigation replays the page-enter animation
          (see index.css). Query-only changes keep the same key, so filtering or
          opening a draft within a screen does not restart it.
        */}
        <div key={location.pathname} className="page-enter">
          <Routes>
            <Route path="/" element={<HomeScreen />} />
          <Route path="/db/:databaseId" element={<MenuScreen />} />
          <Route path="/db/:databaseId/scan" element={<ScanScreen />} />
          <Route path="/db/:databaseId/drafts" element={<DraftsScreen />} />
          <Route path="/db/:databaseId/history" element={<HistoryScreen />} />
          <Route
            path="/settings/accurate"
            element={
              <RequireRole allow={ADMIN_ROLES}>
                <AccurateSettingsScreen />
              </RequireRole>
            }
          />
          <Route
            path="/settings/team"
            element={
              <RequireRole allow={ADMIN_ROLES}>
                <TeamScreen />
              </RequireRole>
            }
          />
          {/*
             Billing is administrative: plans, prices and the upgrade flow.
             Staff and supervisors scan and receive goods; the quota bar in the
             header already tells them what they need to know about limits.
           */}
          <Route
            path="/billing"
            element={
              <RequireRole allow={ADMIN_ROLES}>
                <BillingScreen />
              </RequireRole>
            }
          />
          <Route
            path="/billing/pay/:number"
            element={
              <RequireRole allow={[UserRole.Owner]}>
                <MockPayScreen />
              </RequireRole>
            }
          />
          {/* Every role, because every role has a password to change. */}
          <Route path="/akun" element={<AccountScreen />} />
          <Route path="/verify" element={<VerifyEmailScreen />} />
          <Route path="*" element={<Navigate to="/" replace />} />
          </Routes>
        </div>
      </main>
      <footer className="no-print py-6 text-center text-[11px] font-semibold text-white/30">
        {t('app.footer')}
      </footer>
    </div>
  );
}

function TopBar() {
  const { user, tenant, logout, databases } = useSession();
  const navigate = useNavigate();
  const location = useLocation();
  const { databaseId } = useParams<{ databaseId: string }>();
  const [usage, setUsage] = useState<Usage | null>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [loggingOut, setLoggingOut] = useState(false);
  const t = useT();

  // Re-read usage on navigation so the quota bar reflects a receipt just posted.
  useEffect(() => {
    api
      .get<Usage>('/billing/usage')
      .then(setUsage)
      .catch(() => setUsage(null));
  }, [location.pathname]);

  const activeDb = databases.find((d) => d.id === databaseId);
  const isHome = location.pathname === '/';
  const canAdmin = user !== null && ADMIN_ROLES.includes(user.role);

  return (
    <header className="no-print sticky top-0 z-40 bg-brand-700 text-white shadow-lg">
      <div className="mx-auto flex max-w-7xl flex-wrap items-center gap-4 px-4 py-2.5">
        {/* Home is the first screen — there is nothing behind it to go back to. */}
        {!isHome ? (
          <button
            onClick={() => navigate(-1)}
            aria-label={t('nav.backAria')}
            className="flex h-8 w-8 shrink-0 cursor-pointer items-center justify-center rounded-full border-0 bg-white font-bold text-slate-800 hover:bg-slate-200"
          >
            ←
          </button>
        ) : null}

        <Link to="/" className="flex items-center gap-2 no-underline">
          <span className="text-lg font-black text-white">DR Inbound</span>
          {activeDb ? (
            <span className="rounded bg-white/15 px-2 py-0.5 text-xs font-bold">{activeDb.alias}</span>
          ) : null}
        </Link>

        <div className="ml-auto flex items-center gap-4">
          {usage ? <UsageBar used={usage.rowsUsed} limit={usage.rowsLimit} warn={usage.warn} /> : null}

          <LocaleSwitch />

          <div className="relative">
            <button
              onClick={() => setMenuOpen((v) => !v)}
              className="flex cursor-pointer items-center gap-2 rounded-lg border-0 bg-white/10 px-3 py-1.5 text-sm font-bold text-white hover:bg-white/20"
            >
              <span className="max-w-32 truncate">{user?.name}</span>
              <span className="text-[10px]">▼</span>
            </button>

            {menuOpen ? (
              <>
                {/* Click-away layer so the menu closes on any outside tap. */}
                <div className="fixed inset-0 z-10" onClick={() => setMenuOpen(false)} />
                <div className="absolute right-0 z-20 mt-2 w-56 overflow-hidden rounded-xl bg-white shadow-2xl">
                  <div className="border-b border-slate-100 px-4 py-3">
                    <div className="truncate text-sm font-bold text-slate-800">{tenant?.name}</div>
                    <div className="truncate text-[11px] text-slate-500">{user?.email}</div>
                    {/* The label, not the raw enum value: 'staff' is what the
                        database stores, 'Staf' is what the reader is. */}
                    <div className="mt-1 text-[10px] font-bold tracking-wide text-slate-400 uppercase">
                      {user ? ROLE_LABEL[user.role] : ''}
                    </div>
                  </div>
                  <nav className="flex flex-col py-1">
                    {canAdmin ? (
                      <>
                        <MenuLink to="/settings/accurate" onClick={() => setMenuOpen(false)}>
                          {t('nav.accurate')}
                        </MenuLink>
                        <MenuLink to="/settings/team" onClick={() => setMenuOpen(false)}>
                          {t('nav.team')}
                        </MenuLink>
                      </>
                    ) : null}
                    {canAdmin ? (
                      <MenuLink to="/billing" onClick={() => setMenuOpen(false)}>
                        {t('nav.billing')}
                      </MenuLink>
                    ) : null}
                    <MenuLink to="/akun" onClick={() => setMenuOpen(false)}>
                      {t('nav.account')}
                    </MenuLink>
                    <button
                      disabled={loggingOut}
                      onClick={() => {
                        setLoggingOut(true);
                        setMenuOpen(false);
                        // Navigate explicitly: leaving the user on whatever
                        // deep URL they were on renders the signed-out tree
                        // at a path that may mean nothing without a session.
                        void logout().then(() => navigate('/login', { replace: true }));
                      }}
                      className="cursor-pointer border-0 bg-transparent px-4 py-2 text-left text-sm font-semibold text-red-600 hover:bg-red-50 disabled:opacity-50"
                    >
                      {loggingOut ? t('nav.loggingOut') : t('nav.logout')}
                    </button>
                  </nav>
                </div>
              </>
            ) : null}
          </div>
        </div>
      </div>

      {usage?.exhausted ? (
        <div className="bg-red-600 px-4 py-1.5 text-center text-xs font-bold text-white">
          {t('quota.exhausted')}{' '}
          {/* Only offer the upgrade to someone who can actually do it.
              Sending a staff member to a page they are not allowed to open
              turns a clear warning into a dead end. */}
          {canAdmin ? (
            <Link to="/billing" className="underline">
              {t('quota.upgrade')}
            </Link>
          ) : (
            <span>{t('quota.askAdmin')}</span>
          )}
        </div>
      ) : null}
    </header>
  );
}

function MenuLink({
  to,
  onClick,
  children,
}: {
  to: string;
  onClick: () => void;
  children: React.ReactNode;
}) {
  return (
    <Link
      to={to}
      onClick={onClick}
      className="px-4 py-2 text-sm font-semibold text-slate-700 no-underline hover:bg-slate-50"
    >
      {children}
    </Link>
  );
}
